"use client";

import React, { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import ExportedImage from "next-image-export-optimizer";
import { useTranslations } from "@/lib/i18n-client";
import { track } from "@/lib/analytics";
import { Search, ShieldCheck, Zap, Plane, ArrowRight } from "lucide-react";

/**
 * SkyLane hero — search-LED (the brand's core hypothesis vs the baseline /
 * eSIMCobra deal-led heroes). Destination search dominates; premium airport
 * "land connected" framing + phone mockup + trust badges. Routes to the shared
 * /destinations/ page.
 *
 * Market-test experiment_ids (market-test-plan.md §3, research/07 CF1):
 *   - wf_homepage_search_first_v1      → search-led above-the-fold (exposure + submit)
 *   - copy_arrival_readiness_vs_price_v1 → calm arrival-readiness copy (exposure)
 */
const SEARCH_EXPERIMENT_ID = "wf_homepage_search_first_v1";
const COPY_EXPERIMENT_ID = "copy_arrival_readiness_vs_price_v1";

export default function Hero() {
  const { t } = useTranslations();
  const router = useRouter();
  const [q, setQ] = useState("");

  // Exposure events (consent-gated, best-effort; `track` dedupes by event_id).
  useEffect(() => {
    for (const id of [SEARCH_EXPERIMENT_ID, COPY_EXPERIMENT_ID]) {
      try {
        track({
          event_name: "hero_variant_viewed",
          event_id: `hero_variant_viewed:${id}`,
          experiment_id: id,
        });
      } catch {
        /* analytics is best-effort */
      }
    }
  }, []);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    try {
      track({
        event_name: "destination_search_submitted",
        event_id: `destination_search_submitted:${SEARCH_EXPERIMENT_ID}:${Date.now()}`,
        experiment_id: SEARCH_EXPERIMENT_ID,
      });
    } catch {
      /* analytics is best-effort */
    }
    router.push("/destinations/");
  };

  return (
    <section className="sky-wash relative overflow-hidden">
      <div className="container mx-auto px-4 pt-12 pb-16 lg:pt-20 lg:pb-24 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3 py-1 text-sm font-semibold">
            <Plane className="w-4 h-4" /> {t("sky.hero.eyebrow", "Ready before arrival")}
          </span>

          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl text-foreground">
            {t("sky.hero.title", "Land connected.")}
          </h1>

          <p className="mt-5 text-lg text-muted-foreground max-w-xl">
            {t(
              "sky.hero.subtitle",
              "Set up your travel eSIM before you board, then step off the plane already online in 180+ countries. No SIM line, no roaming surprises.",
            )}
          </p>

          <form onSubmit={onSubmit} className="mt-7 flex items-stretch gap-2 max-w-md">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                type="text"
                placeholder={t("sky.hero.searchPlaceholder", "Where are you flying?")}
                aria-label={t("sky.hero.searchAria", "Search destinations")}
                className="w-full h-12 rounded-xl border border-input bg-card pl-10 pr-3 text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <button
              type="submit"
              className="group inline-flex items-center gap-2 h-12 rounded-xl bg-primary text-primary-foreground px-5 font-semibold shadow-sm hover:bg-primary/90 transition-all active:scale-[0.98] shrink-0"
            >
              {t("sky.hero.searchCta", "Find my eSIM")} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </button>
          </form>

          <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-foreground/80">
            <li className="inline-flex items-center gap-1.5"><Zap className="w-4 h-4 text-primary" />{t("sky.hero.badge1", "Instant email delivery")}</li>
            <li className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-primary" />{t("sky.hero.badge2", "Secure checkout")}</li>
            <li className="inline-flex items-center gap-1.5"><Plane className="w-4 h-4 text-primary" />{t("sky.hero.badge3", "Works when you land")}</li>
          </ul>
        </div>

        <div className="flex justify-center lg:justify-end mt-10 lg:mt-0">
              <div className="relative w-[300px] sm:w-[360px] lg:w-[420px] aspect-square animate-float z-10 bg-white/95 rounded-[2.5rem] shadow-2xl ring-1 ring-black/5 overflow-hidden p-6">
            <div className="relative w-full h-full rounded-2xl overflow-hidden bg-white/50">
              <ExportedImage
                src="/images/brands/airport-fast-lane/skylane_hero_mockup.webp"
                alt={t("sky.hero.mockupAlt", "Smartphone displaying eSIM connection status over boarding ticket")}
                fill
                priority
                className="object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
